import { some, startCase } from "lodash";
import * as React from "react";

import { formatDateTime, InfoIndicator } from "@app/shared";
import { preventDefaultMouseEvent } from "@app/shared/utility";
import { RoleRestricted, Roles } from "@app/user";
import { ResultRegion, SpecialtyGroupException, SpecialtyGroupExceptionStatus } from "@app/projects/results/common";
import { SpecialtyGroupExceptionHistoryTimeline } from "@app/projects/network-assessment/SpecialtyGroupExceptionHistoryTimeline";
import { FlyoutLink } from "@app/shared/Flyouts";
import moment from "moment";
import ExceptionFlyout from "@app/projects/network-assessment/SpecialtyGroupExceptionFlyout";

interface SpecialtyGroupExceptionEditorProps {
  editEnabled: boolean;
  region: ResultRegion;
  specialtyGroupCode: string;
  specialtyGroupName: string;
  exception: SpecialtyGroupException;
  onSubmit: (exception: SpecialtyGroupException) => void;
}

export default class SpecialtyGroupExceptionEditor extends React.Component<SpecialtyGroupExceptionEditorProps> {
  constructor(props: SpecialtyGroupExceptionEditorProps) {
    super(props);
  }

  public render() {
    return (
      <>
        {this.shouldShowException() ? (
          this.renderException()
        ) : this.props.editEnabled &&
        (
          <div className="specialty-group-exception-editor-trigger">
            <RoleRestricted allowedRoles={[Roles.EXCEPTION_MANAGER]}>
              <FlyoutLink
                classNames="qes-button-secondary"
                content={{
                  body: this.renderForm(),
                  title: "Specialty Exception Information",
                }}
                testId="specialty-group-exception-editor__enter-exception-flyout"
              >
                Enter an Exception
              </FlyoutLink>
            </RoleRestricted>
          </div>
        )
        }
      </>
    );
  }

  private renderException() {
    if (!this.props.exception) {
      return null;
    }
    const status = startCase(this.props.exception.specialtyGroupExceptionStatus);
    const date = formatDateTime(this.props.exception.dateUpdated);
    const startDate = this.props.exception.startDate ? moment(this.props.exception.startDate).format("MM/DD/YYYY") : '---';
    const endDate = this.props.exception.endDate ? moment(this.props.exception.endDate).format("MM/DD/YYYY") : '---';
    const previousApproval = this.props.exception.previouslyApproved ? "Previously Approved" : "NOT Previously Approved";
    const comment = this.props.exception.comment;
    const exceptionNarrative = this.props.exception.exceptionNarrative;

    return (
      <div className="specialty-group-exception-editor-status">
        <div>
          <span className="pr10">Exception: <strong>{status}</strong>, {previousApproval}</span>
          <span> - </span>
          <span className="pl10 pr10">{date}</span>
          {this.shouldShowExceptionHistory() &&
            <span className="pull-right">
              <FlyoutLink
                classNames="specialty-history-link mt-2 text-dark p-0"
                content={{
                  body: <SpecialtyGroupExceptionHistoryTimeline specialtyGroupException={this.props.exception} />,
                  title: "Specialty Exception History",
                }}
                testId="specialty-group-exception-editor__exception-history-flyout"
              >
                <InfoIndicator className={"fa fa-history"} tooltipTitle={"Show Exception Timeline Details"} tooltipPlacement={"bottom"} />
              </FlyoutLink>
            </span>
          }
          {this.props.editEnabled &&
            (
              <RoleRestricted allowedRoles={[Roles.EXCEPTION_MANAGER]}>
                <span className="pull-right mr10">
                  <FlyoutLink
                    classNames="qes-button-secondary"
                    content={{
                      body: this.renderForm(),
                      title: "Specialty Exception Information",
                    }}
                    testId="specialty-group-exception-editor__edit-exception-flyout"
                  >
                    Edit
                  </FlyoutLink>
                  <a className="qes-button qes-button-secondary" type="button" href="#" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, this.handleDelete)}>Archive</a>
                </span>
              </RoleRestricted>
            )
          }

        </div>
        <div>
          Processed: {this.props.exception.isUnprocessed ? 'No' : 'Yes'}
        </div>
        {startDate && startDate !== '' && startDate !== '---' && (
          <div className="text-muted pt5">
            Start Date: {startDate}
          </div>
        )}
        {endDate && endDate !== '' && endDate !== '---' && (
          <div className="text-muted pt5">
            Expiration Date: {endDate}
          </div>
        )}
        {comment && (
          <div className="text-muted pt5">
            Comment: {comment}
          </div>
        )}
        {exceptionNarrative && (
          <div className="text-muted pt5">
            Exception Narrative: {exceptionNarrative}
          </div>
        )}
      </div>
    );
  }

  private renderForm() {
    return (
      <ExceptionFlyout
        exception={this.props.exception}
        specialtyGroupName={this.props.specialtyGroupName}
        specialtyGroupCode={this.props.specialtyGroupCode}
        region={this.props.region}
        handleSubmit={this.handleSubmit}
      />
    );
  }

  private shouldShowException = () => {
    return this.props.exception
      && this.props.exception.specialtyGroupExceptionStatus
      && this.shouldShowStatus(this.props.exception.specialtyGroupExceptionStatus);
  }

  private shouldShowExceptionHistory = () => {
    return this.shouldShowException()
      && this.props.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.None
      && this.props.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived;
  }

  private shouldShowStatus = (value: SpecialtyGroupExceptionStatus) => {
    const statuses = [
      SpecialtyGroupExceptionStatus.Pending,
      SpecialtyGroupExceptionStatus.Submitted,
      SpecialtyGroupExceptionStatus.Approved,
      SpecialtyGroupExceptionStatus.Denied,
      SpecialtyGroupExceptionStatus.Appealed,
    ];
    return some(statuses, (s) => s === value);
  }

  private handleSubmit = (exceptionToSubmit: SpecialtyGroupException) => {
    this.props.onSubmit(exceptionToSubmit);
  }

  private handleDelete = () => {
    const exception: SpecialtyGroupException = {
      ...this.props.exception,
      comment: "",
      dateUpdated: new Date(),
      exceptionNarrative: "",
      previouslyApproved: false,
      specialtyGroupExceptionStatus: SpecialtyGroupExceptionStatus.Archived,
    };
    this.props.onSubmit(exception);

    this.setState((prevState) => ({
      ...prevState,
      comment: "",
      exceptionNarrative: "",
      previouslyApproved: false,
      status: SpecialtyGroupExceptionStatus.Pending,
    }));
  }
}
